<template>
  <div>
    <div v-if="isLoading" class="loading-overlay">
      <v-progress-circular
          indeterminate
          size="60"
          color="primary"
      />
      <div class="loading-text">
        {{ saving ? 'Saving...' : 'Loading...' }}
      </div>
    </div>
    <div v-else>
      <h1 class="text-center mt-3">Target Email</h1>
      <div class="p-5 bg-white rounded">

        <!-- Audit Log Section -->
        <section class="information-group mb-5">
          <div class="row information-title-wrapper mx-0 my-0 px-2">
            <div class="information-title">Audit Log</div>
          </div>
          <div v-if="loadingAudit" class="tab-loading">
            <v-progress-circular indeterminate size="40" />
          </div>
          <div class="form p-3 bg-white rounded">
            <p v-if="successStatus.audit" class="txt-success">
              {{ successStatus.audit }}
            </p>
            <p v-if="errorStatus.audit" class="txt-error">
              {{ errorStatus.audit }}
            </p>
            <template v-for="(email, index) in auditLogEmails">
              <div class="email-row flex-column align-items-start">
                <div class="d-flex align-items-center w-100">
                  <input
                      :id="`audit-email-${index}`"
                      :name="`email-${index}`"
                      v-model="auditLogEmails[index]"
                      type="email"
                      class="form-control w-50 mr-3"
                      :class="{ 'is-invalid': emailFieldErrors.audit[index] }"
                      placeholder="email@example.com"
                  />
                  <button
                      class="btn btn-outline-danger btn-sm"
                      @click="removeAuditLogEmails(index)"
                  >
                    Delete
                  </button>
                </div>

                <small
                    v-if="emailFieldErrors.audit[index]"
                    class="text-danger mt-1"
                >
                  {{ emailFieldErrors.audit[index] }}
                </small>
              </div>
            </template>


            <button class="btn-add" @click="addAuditLogEmails">
              + Add Email
            </button>

            <div class="d-flex justify-content-end mt-5">
              <button
                  class="btn-save"
                  :disabled="saving"
                  @click="saveEmails('audit')"
              >
                {{ saving ? 'Saving...' : 'Save Changes' }}
              </button>
            </div>
          </div>
        </section>

        <!-- Cloud Watch Section -->
        <section class="information-group">
          <div class="row information-title-wrapper mx-0 my-0 px-2">
            <div class="information-title">Suspicious Activity</div>
          </div>
          <div v-if="loadingCloudWatch" class="tab-loading">
            <v-progress-circular indeterminate size="40" />
          </div>
          <div class="form p-3 bg-white rounded">
            <p v-if="successStatus.cloudWatch" class="txt-success">
              {{ successStatus.cloudWatch }}
            </p>
            <p v-if="errorStatus.cloudWatch" class="txt-error">
              {{ errorStatus.cloudWatch }}
            </p>
            <template v-for="(email, index) in cloudWatchEmails">
              <div class="email-row flex-column align-items-start">
                <div class="d-flex align-items-center w-100">
                  <input
                      :id="`cloudwatch-email-${index}`"
                      :name="`email-${index}`"
                      v-model="cloudWatchEmails[index]"
                      type="email"
                      class="form-control w-50 mr-3"
                      :class="{ 'is-invalid': emailFieldErrors.cloudWatch[index] }"
                      placeholder="email@example.com"
                  />

                  <button
                      class="btn btn-outline-danger btn-sm"
                      @click="removeCloudWatchEmail(index)"
                  >
                    Delete
                  </button>
                </div>

                <small
                    v-if="emailFieldErrors.cloudWatch[index]"
                    class="text-danger mt-1"
                >
                  {{ emailFieldErrors.cloudWatch[index] }}
                </small>
              </div>
            </template>

            <button class="btn-add" @click="addCloudWatchEmail">
              + Add Email
            </button>

            <div class="d-flex justify-content-end mt-5">
              <button
                  class="btn-save"
                  :disabled="saving"
                  @click="saveEmails('cloudWatch')"
              >
                {{ saving ? 'Saving...' : 'Save Changes' }}
              </button>
            </div>
          </div>
        </section>

      </div>
    </div>
  </div>
</template>

<script>
import EmailService from "services/api/email.service";

export default {
  name: "NotificationSettings",

  data() {
    return {
      auditLogEmails: [],
      cloudWatchEmails: [],
      loadingAudit: false,
      loadingCloudWatch: false,
      saving: false,

      successStatus: {
        audit: "",
        cloudWatch: ""
      },

      errorStatus: {
        audit: "",
        cloudWatch: ""
      },

      emailFieldErrors: {
        audit: {},
        cloudWatch: {}
      },

    };
  },

  computed: {
    isLoading() {
      return this.loadingAudit || this.loadingCloudWatch || this.saving;
    }
  },

  mounted() {
    this.fetchEmails("audit");
    this.fetchEmails("cloudWatch");
  },

  methods: {
    async fetchEmails(type) {
      this.errorStatus[type] = "";

      if (type === "audit") this.loadingAudit = true;
      else this.loadingCloudWatch = true;

      try {
        const res =
            type === "audit"
                ? await EmailService.getAuditLogEmailList()
                : await EmailService.getCloudWatchEmailList();

        if (type === "audit") {
          this.auditLogEmails = res.data.emails || [];
        } else {
          this.cloudWatchEmails = res.data.emails || [];
        }
      } catch (err) {
        const errorData = err?.response?.data;
        this.errorStatus[type] =
            errorData?.message || `Failed to fetch ${type} emails. Please try again.`;
      } finally {
        if (type === "audit") this.loadingAudit = false;
        else this.loadingCloudWatch = false;
      }
    },

    addAuditLogEmails() {
      this.auditLogEmails.push("");
    },

    addCloudWatchEmail() {
      this.cloudWatchEmails.push("");
    },

    removeAuditLogEmails(index) {
      this.auditLogEmails.splice(index, 1);
    },

    removeCloudWatchEmail(index) {
      this.cloudWatchEmails.splice(index, 1);
    },

    async saveEmails(type) {
      this.resetErrorText();
      this.emailFieldErrors[type] = {};
      this.errorStatus[type] = "";
      this.successStatus[type] = "";

      const emailList = type === "audit" ? this.auditLogEmails : this.cloudWatchEmails;

      if (emailList.some((e) => !e)) {
        this.errorStatus[type] = "Email field cannot be empty.";
        return;
      }

      this.saving = true;

      try {
        const res =
            type === "audit"
                ? await EmailService.saveAuditLogEmail(this.auditLogEmails)
                : await EmailService.saveCloudWatchEmail(this.cloudWatchEmails);

        if (type === "audit") {
          this.auditLogEmails = res.data.emails;
        } else {
          this.cloudWatchEmails = res.data.emails;
        }
        if (res.data.status === "failure") {
          alert("Failed to save emails. Please try again.");
        }
        this.successStatus[type] = "Successfully saved emails.";
      } catch (err) {
        const errorData = err.response?.data;

        if (errorData?.status === "failure" && errorData.errors?.length) {
          this.setErrorText(type, errorData.errors);
        } else if (errorData?.message) {
          this.errorStatus[type] = errorData.message;
        }
      } finally {
        this.saving = false;
      }
    },

    setErrorText(type, errors) {
      errors.forEach((err) => {
        if (err.fieldName?.startsWith("email-")) {
          const index = Number(err.fieldName.split("-")[1]);
          this.$set(this.emailFieldErrors[type], index, err.message);
        }
      });
    },

    resetErrorText() {
      this.errorStatus.audit = "";
      this.errorStatus.cloudWatch = "";
    }
  }
};
</script>

<style scoped>
.email-row {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.btn-add {
  margin-top: 8px;
  background: none;
  border: none;
  color: #1976d2;
  cursor: pointer;
}

.btn-save {
  padding: 8px 16px;
  background: #1976d2;
  color: #fff;
  border: none;
  border-radius: 4px;
}

.txt-success {
  margin-top: 12px;
  color: #2e7d32;
}

.txt-error {
  margin-top: 12px;
  color: #d32f2f;
}

.loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.loading-text {
  margin-top: 12px;
  font-weight: 500;
}

.tab-loading {
  min-height: 200px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.information-title-wrapper {
  background-color: #2a2a2e;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}

.information-title {
  font-weight: 600;
  font-size: 17px;
  padding: 14px;
  color: #dcdcdc;
}

.information-group {
  border-radius: 8px;
  box-shadow: rgba(0, 0, 0, 0.25) 0 3px 8px, rgba(0, 0, 0, 0.22) 0 2px 2px;
}

.is-invalid {
  border-color: #d32f2f;
}
</style>